import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { postData } from "../../utility/Utility";

const UserPolicyModal = ({ show, onClose, userId, selectedPolicyId, onSelect }) => {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const rowsPerPage = 10;

    const fetchPoliciesForUser = async (page = 1, search = "") => {
        if (!userId) return;
        setLoading(true);
        try {
            const response = await postData("api/policies/all", {
                page,
                limit: rowsPerPage,
                userId,
                search,
                status: "approved",
            });

            if (response?.success) {
                // API shape: { success, data: [...], total, page, limit }
                const items = response.data || [];
                const total = response.total || items.length;
                const limit = response.limit || rowsPerPage;

                setPolicies(items);
                setTotalPages(total > 0 ? Math.ceil(total / limit) : 0);
            } else {
                setPolicies([]);
                setTotalPages(0);
            }
        } catch (err) {
            console.error("Failed to load policies for user", err);
            setPolicies([]);
            setTotalPages(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (show) {
            fetchPoliciesForUser(currentPage + 1, searchTerm);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, userId, currentPage]);

    if (!show) return null;

    return (
        <div
            className="modal fade show"
            style={{ display: show ? "block" : "none" }}
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
        >
            <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
                <div className="modal-content">
                    <div className="modal-header bg-light">
                        <h5 className="modal-title">
                            <i className="mdi mdi-file-document-outline me-2"></i>
                            Select Policy
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={onClose}
                            aria-label="Close"
                        ></button>
                    </div>
                    <div className="modal-body">
                        <div className="mb-3">
                            <div className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by policy number, unique ID, etc."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(0);
                                        // trigger fetch for first page with new search
                                        fetchPoliciesForUser(1, e.target.value);
                                    }}
                                />
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => {
                                        setSearchTerm("");
                                        setCurrentPage(0);
                                        fetchPoliciesForUser(1, "");
                                    }}
                                >
                                    Reset
                                </button>
                            </div>
                        </div>
                        <div className="table-responsive">
                            <table className="table table-sm table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th>S.No</th>
                                        <th>Policy</th>
                                        <th>Plan</th>
                                        <th>Amount</th>
                                        <th>Agent</th>
                                        <th>Buyer</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr>
                                            <td colSpan="7" className="text-center py-3">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : policies.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="text-center text-muted py-3">
                                                No policies found
                                            </td>
                                        </tr>
                                    ) : (
                                        policies.map((policy, index) => (
                                            <tr
                                                key={policy._id}
                                                className={
                                                    policy._id === selectedPolicyId ? "table-primary" : ""
                                                }
                                            >
                                                <td>
                                                    {currentPage * rowsPerPage + index + 1}
                                                </td>
                                                <td>
                                                    <div className="fw-semibold">
                                                        {policy.policyNumber || policy.uniqueId || "Policy"}
                                                    </div>
                                                    <div className="text-muted small">
                                                        {policy.partnerId?.name || policy.companyName || ""}
                                                    </div>
                                                </td>
                                                <td className="small">{policy.insurance_plan || "-"}</td>
                                                <td className="small">
                                                    {policy.sale_amount ? `₹${policy.sale_amount}` : "-"}
                                                </td>
                                                <td className="small">
                                                    {policy.agent_id ? (
                                                        <>
                                                            <div className="fw-semibold">
                                                                {policy.agent_id.fullName || "-"}
                                                            </div>
                                                            <div className="text-muted small">
                                                                {policy.agent_id.uniqueId || policy.agent_id.email || policy.agent_id.mobile || ""}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td className="small">
                                                    {policy.user_id ? (
                                                        <>
                                                            <div className="fw-semibold">
                                                                {policy.user_id.fullName || "-"}
                                                            </div>
                                                            <div className="text-muted small">
                                                                {policy.user_id.uniqueId || policy.user_id.email || policy.user_id.mobile || ""}
                                                            </div>
                                                        </>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => onSelect && onSelect(policy._id)}
                                                    >
                                                        Select
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <ReactPaginate
                                previousLabel={<i className="mdi mdi-chevron-left"></i>}
                                nextLabel={<i className="mdi mdi-chevron-right"></i>}
                                breakLabel="..."
                                breakClassName="page-item"
                                breakLinkClassName="page-link"
                                pageCount={totalPages}
                                marginPagesDisplayed={1}
                                pageRangeDisplayed={3}
                                onPageChange={({ selected }) => setCurrentPage(selected)}
                                containerClassName="pagination pagination-rounded justify-content-end mt-3 mb-0"
                                pageClassName="page-item"
                                pageLinkClassName="page-link"
                                previousClassName="page-item"
                                previousLinkClassName="page-link"
                                nextClassName="page-item"
                                nextLinkClassName="page-link"
                                activeClassName="active"
                                forcePage={currentPage}
                            />
                        )}
                    </div>
                    <div className="modal-footer bg-light">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserPolicyModal;
